import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Copy, Check, ExternalLink, Link2, Pencil, Users } from "lucide-react";
import { toast } from "sonner";

export default function ProfileLinkCard() {
  const [handle, setHandle] = useState(null);
  const [followers, setFollowers] = useState(0);
  const [copied, setCopied] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    api.get("/filmmaker/handle").then((r) => { setHandle(r.data.handle); setFollowers(r.data.follower_count || 0); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (inputRef.current) inputRef.current.scrollLeft = inputRef.current.scrollWidth;
  }, [handle]);

  if (!handle) return null;
  const link = `${window.location.origin}/@${handle}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Profile link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy. Long-press the field to copy manually.");
    }
  };

  return (
    <div className="rounded-xl p-6 bg-gradient-to-br from-terracotta/15 via-gold/5 to-transparent border border-terracotta/30 relative overflow-hidden" data-testid="profile-link-card">
      <div className="absolute -right-10 -top-10 w-44 h-44 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
      <div className="relative">
        <p className="text-xs uppercase tracking-widest text-gold font-semibold mb-1 flex items-center gap-1.5">
          <Link2 className="w-3 h-3" /> Your profile link
        </p>
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display text-xl font-bold">Share your Cinevoya page</h3>
          <div className="text-right shrink-0" data-testid="dashboard-follower-count">
            <p className="font-display font-bold text-2xl text-gold flex items-center gap-1.5 justify-end"><Users className="w-4 h-4" />{followers.toLocaleString()}</p>
            <p className="text-[11px] uppercase tracking-widest text-foreground/50">{followers === 1 ? "follower" : "followers"}</p>
          </div>
        </div>
        <p className="text-foreground/70 text-sm mt-1 mb-4">
          Viewers who open this link land on your public profile — your photo, bio and full film catalogue — ready to watch.
        </p>
        <div className="flex items-stretch gap-2 mb-3">
          <input
            ref={inputRef}
            value={link}
            readOnly
            onFocus={(e) => e.target.select()}
            className="flex-1 min-w-0 px-3 h-10 text-sm rounded-md bg-ink border border-white/10 font-mono"
            data-testid="profile-link-input"
          />
          <Button onClick={copy} className="bg-terracotta hover:bg-ember text-white h-10 px-4" data-testid="profile-link-copy-btn">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>
        <div className="flex flex-wrap gap-4 text-sm">
          <Link to={`/@${handle}`} className="inline-flex items-center gap-1.5 text-terracotta hover:text-ember transition-colors" data-testid="profile-link-view">
            <ExternalLink className="w-3.5 h-3.5" /> View profile
          </Link>
          <Link to="/filmmaker/profile" className="inline-flex items-center gap-1.5 text-foreground/60 hover:text-foreground transition-colors" data-testid="profile-link-edit-handle">
            <Pencil className="w-3.5 h-3.5" /> Change handle
          </Link>
        </div>
      </div>
    </div>
  );
}
